import { HarnessRole, RunEvent } from 'types';

import { api } from './api';

/**
 * Minimal scenario summary returned by `GET /internal/accountctl/scenarios`.
 * `preconditions` is intentionally loose — the UI only renders it as JSON.
 */
export interface AccountctlScenarioSummary {
  id: string;
  description: string;
  preconditions?: Record<string, unknown>;
}

/** Summary row returned by `GET /internal/accountctl/runs`. */
export interface AccountctlRunSummary {
  id: string;
  scenarioId: string;
  status: 'running' | 'ok' | 'error' | 'cancelled';
  startedAt: string;
  startedByEmail: string;
  accountId?: string;
  finishedAt?: string;
  flagged?: boolean;
  flagNote?: string;
  adHocDescription?: string;
  /** Terminal failure reason. Absent on `ok` runs and on historical docs predating this field. */
  error?: string;
}

/** Full run document returned by `GET /internal/accountctl/runs/:id`. */
export interface AccountctlRunDetail extends AccountctlRunSummary {
  startedByUserId: string;
  params: Record<string, unknown>;
  events: RunEvent[];
  flagged?: boolean;
  flagNote?: string;
  flaggedAt?: string;
  flaggedByEmail?: string;
}

export interface AccountctlTestUser {
  label: string;
  role: HarnessRole;
  email: string;
  password: string;
  organizationAlias?: string;
}

interface CreateRunArgs {
  scenarioId: string;
  params?: Record<string, unknown>;
}

interface CreateRunResponse {
  runId: string;
}

interface ListRunsArgs {
  limit?: number;
}

interface ListRunsResponse {
  runs: AccountctlRunSummary[];
}

interface ListScenariosResponse {
  scenarios: AccountctlScenarioSummary[];
}

interface GetTestUsersResponse {
  users: AccountctlTestUser[];
}

interface PutTestUsersArgs {
  users: AccountctlTestUser[];
}

interface TestTestUserArgs {
  index: number;
}

interface TestTestUserResponse {
  ok: boolean;
  error?: string;
  organizationAlias?: string;
}

interface CancelRunResponse {
  cancelled: boolean;
}

interface ImportRunArgs {
  dump: Record<string, unknown>;
}

interface ImportRunResponse {
  runId: string;
}

interface ComposeRunArgs {
  description: string;
}

export interface ComposeRunResponse {
  runId: string;
  source: 'matched' | 'composed';
  scenarioId?: string;
}

interface OrgctlFixArgs {
  scenarioId: string;
}

export interface OrgctlFixFailure {
  category: string;
  message: string;
  fixable: boolean;
}

export interface OrgctlFixResponse {
  allClear: boolean;
  remaining: OrgctlFixFailure[];
}

/**
 * RTK Query slice for the internal accountctl routes. Mounted at
 * `/api/internal/accountctl/*` server-side; the base query already prefixes
 * `/api`.
 *
 * SSE for run events is intentionally NOT modeled here — see
 * `useRunStream.ts` for the EventSource hook that drives the live timeline.
 * The run-detail query is still useful for the initial snapshot before SSE
 * attaches, and to refresh after `run.finished`.
 */
export const accountctlApi = api.injectEndpoints({
  endpoints: (build) => ({
    listAccountctlScenarios: build.query<AccountctlScenarioSummary[], void>({
      query: () => ({ url: '/internal/accountctl/scenarios' }),
      transformResponse: (res: ListScenariosResponse) => res.scenarios,
    }),
    listAccountctlRuns: build.query<AccountctlRunSummary[], ListRunsArgs | void>({
      query: (args) => ({
        url: '/internal/accountctl/runs',
        params: args?.limit ? { limit: args.limit } : undefined,
      }),
      transformResponse: (res: ListRunsResponse) => res.runs,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'AccountctlRuns' as const, id })),
              { type: 'AccountctlRuns' as const, id: 'LIST' },
            ]
          : [{ type: 'AccountctlRuns' as const, id: 'LIST' }],
    }),
    getAccountctlRun: build.query<AccountctlRunDetail, string>({
      query: (id) => ({ url: `/internal/accountctl/runs/${id}` }),
      providesTags: (_result, _err, id) => [{ type: 'AccountctlRuns' as const, id }],
    }),
    createAccountctlRun: build.mutation<CreateRunResponse, CreateRunArgs>({
      query: (body) => ({
        url: '/internal/accountctl/runs',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'AccountctlRuns' as const, id: 'LIST' }],
    }),
    cancelAccountctlRun: build.mutation<CancelRunResponse, string>({
      query: (id) => ({
        url: `/internal/accountctl/runs/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _err, id) => [{ type: 'AccountctlRuns' as const, id }],
    }),
    getAccountctlTestUsers: build.query<AccountctlTestUser[], void>({
      query: () => ({ url: '/internal/accountctl/test-users' }),
      transformResponse: (res: GetTestUsersResponse) => res.users,
      providesTags: [{ type: 'AccountctlTestUsers' as const, id: 'LIST' }],
    }),
    putAccountctlTestUsers: build.mutation<AccountctlTestUser[], PutTestUsersArgs>({
      query: (body) => ({
        url: '/internal/accountctl/test-users',
        method: 'PUT',
        body,
      }),
      transformResponse: (res: GetTestUsersResponse) => res.users,
      invalidatesTags: [{ type: 'AccountctlTestUsers' as const, id: 'LIST' }],
    }),
    testAccountctlTestUser: build.mutation<TestTestUserResponse, TestTestUserArgs>({
      query: ({ index }) => ({
        url: `/internal/accountctl/test-users/${index}/test`,
        method: 'POST',
      }),
    }),
    fixAccountctlOrg: build.mutation<OrgctlFixResponse, OrgctlFixArgs>({
      query: (body) => ({
        url: '/internal/accountctl/orgctl/fix',
        method: 'POST',
        body,
      }),
    }),
    flagAccountctlRun: build.mutation<{ ok: boolean }, { id: string; note?: string }>({
      query: ({ id, note }) => ({
        url: `/internal/accountctl/runs/${id}/flag`,
        method: 'POST',
        body: note !== undefined ? { note } : {},
      }),
      invalidatesTags: (_result, _err, { id }) => [{ type: 'AccountctlRuns' as const, id }],
    }),
    composeAccountctlRun: build.mutation<ComposeRunResponse, ComposeRunArgs>({
      query: (body) => ({
        url: '/internal/accountctl/compose',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'AccountctlRuns' as const, id: 'LIST' }],
    }),
    importAccountctlRun: build.mutation<ImportRunResponse, ImportRunArgs>({
      query: ({ dump }) => ({
        url: '/internal/accountctl/import',
        method: 'POST',
        body: dump,
      }),
      invalidatesTags: [{ type: 'AccountctlRuns' as const, id: 'LIST' }],
    }),
  }),
});

export const {
  useListAccountctlScenariosQuery,
  useListAccountctlRunsQuery,
  useGetAccountctlRunQuery,
  useCreateAccountctlRunMutation,
  useCancelAccountctlRunMutation,
  useGetAccountctlTestUsersQuery,
  usePutAccountctlTestUsersMutation,
  useTestAccountctlTestUserMutation,
  useFixAccountctlOrgMutation,
  useComposeAccountctlRunMutation,
  useFlagAccountctlRunMutation,
  useImportAccountctlRunMutation,
} = accountctlApi;
